// In-field clear (X) + Escape-to-clear for text inputs and textareas. This is
// the site standard: any text box that can hold a value shows an X on the
// right when non-empty, and Escape clears before it does anything else
// (close a modal, etc.). See ui-ux-standard rule 1 and rule 8.
import {
  forwardRef,
  useMemo,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from "react";
import { XIcon } from "./icons.js";

const CLEAR_BTN =
  "absolute z-10 flex h-6 w-6 items-center justify-center rounded-full text-gray-400 " +
  "hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200 " +
  "after:absolute after:content-[''] after:-inset-0 " +
  "[@media(pointer:coarse)]:after:-inset-2.5";

// Strip the inner element's own padding-right and width/flex utilities: the
// wrapper <div> (not the input) now owns width, and `pr-8` is re-added by the
// caller of this helper to make room for the clear X. Order matters - match
// `min-w-*`/`max-w-*` before the bare `w-*` alternative so `min-w-48` is
// consumed whole and not left as a stray `min-`.
function innerControlClass(className: string): string {
  return (className || "")
    .replace(/\bpr-\S+/g, "")
    .replace(
      /\b(?:min-w-\S+|max-w-\S+|w-\S+|flex-1|flex-auto|flex-none|grow|shrink(?:-0)?)\b/g,
      "",
    )
    .replace(/\s+/g, " ")
    .trim();
}

function wrapperClass(className: string | undefined, extra?: string): string {
  const cls = className ?? "";
  const parts = ["relative"];
  if (extra) parts.push(extra);
  if (cls.includes("flex-1")) {
    parts.push("min-w-0 flex-1");
    const mw = cls.match(/\bmin-w-\S+/g);
    if (mw) parts.push(...mw);
  } else {
    const widths = cls.match(/\b(?:min-w-\S+|max-w-\S+|w-\S+)/g);
    if (widths?.length) parts.push(...widths);
    else parts.push("w-full");
  }
  return parts.join(" ");
}

function emitInputChange(
  onChange: InputHTMLAttributes<HTMLInputElement>["onChange"] | undefined,
  value: string,
) {
  onChange?.({
    target: { value },
    currentTarget: { value },
  } as ChangeEvent<HTMLInputElement>);
}

function emitTextareaChange(
  onChange: TextareaHTMLAttributes<HTMLTextAreaElement>["onChange"] | undefined,
  value: string,
) {
  onChange?.({
    target: { value },
    currentTarget: { value },
  } as ChangeEvent<HTMLTextAreaElement>);
}

export type ClearableInputProps = InputHTMLAttributes<HTMLInputElement> & {
  wrapperClassName?: string;
};

export const ClearableInput = forwardRef<HTMLInputElement, ClearableInputProps>(
  function ClearableInput(
    { className, wrapperClassName, onKeyDown, onChange, value, disabled, readOnly, ...props },
    ref,
  ) {
    const str = value == null ? "" : String(value);
    const showClear = !!str && !disabled && !readOnly;
    const spinnerHideClass =
      props.type === "number"
        ? " [appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none"
        : "";
    const mergedClass =
      `${innerControlClass(className ?? "")} block w-full pr-8 [&::-webkit-search-cancel-button]:hidden${spinnerHideClass}`.trim();

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
      if (e.key === "Escape" && str) {
        e.stopPropagation();
        e.preventDefault();
        emitInputChange(onChange, "");
        return;
      }
      onKeyDown?.(e);
    }

    return (
      <div className={wrapperClass(className, wrapperClassName)}>
        <input
          {...props}
          ref={ref}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          className={mergedClass}
        />
        {showClear && (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear"
            className={`${CLEAR_BTN} right-1.5 top-1/2 -translate-y-1/2`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => emitInputChange(onChange, "")}
          >
            <XIcon size={14} />
          </button>
        )}
      </div>
    );
  },
);

export type ClearableTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  wrapperClassName?: string;
};

export const ClearableTextarea = forwardRef<HTMLTextAreaElement, ClearableTextareaProps>(
  function ClearableTextarea(
    { className, wrapperClassName, onKeyDown, onChange, value, disabled, readOnly, ...props },
    ref,
  ) {
    const str = value == null ? "" : String(value);
    const showClear = !!str && !disabled && !readOnly;
    const mergedClass = `${innerControlClass(className ?? "")} block w-full pr-8`.trim();

    function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
      if (e.key === "Escape" && str) {
        e.stopPropagation();
        e.preventDefault();
        emitTextareaChange(onChange, "");
        return;
      }
      onKeyDown?.(e);
    }

    return (
      <div className={wrapperClass(className, wrapperClassName)}>
        <textarea
          {...props}
          ref={ref}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          className={mergedClass}
        />
        {showClear && (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear"
            className={`${CLEAR_BTN} right-1.5 top-1.5`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => emitTextareaChange(onChange, "")}
          >
            <XIcon size={14} />
          </button>
        )}
      </div>
    );
  },
);

/** Free-text input with a suggestion list of previously used values. Typing
 *  filters the list; picking a row fills the field; a new value is always
 *  allowed. Includes the standard in-field clear X. */
export function AutocompleteInput({
  suggestions,
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  ...rest
}: Omit<ClearableInputProps, "value" | "onChange"> & {
  suggestions: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return suggestions;
    return suggestions.filter((s) => s.toLowerCase().includes(q));
  }, [suggestions, value]);

  const showList = open && filtered.length > 0;

  function select(next: string) {
    onChange(next);
    setOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (showList && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      const delta = e.key === "ArrowDown" ? 1 : -1;
      setHighlight((i) => (i + delta + filtered.length) % filtered.length);
      return;
    }
    if (showList && e.key === "Enter") {
      const pick = filtered[highlight];
      if (pick) {
        e.preventDefault();
        select(pick);
        return;
      }
    }
    if (e.key === "Escape" && !value && open) {
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);
      return;
    }
    onKeyDown?.(e);
  }

  return (
    <div className="relative w-full">
      <ClearableInput
        {...rest}
        value={value}
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-autocomplete="list"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={(e) => {
          setOpen(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          window.setTimeout(() => setOpen(false), 150);
          onBlur?.(e);
        }}
        onKeyDown={handleKeyDown}
      />
      {showList && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          {filtered.map((s, i) => (
            <li key={s} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(s)}
                onMouseEnter={() => setHighlight(i)}
                className={
                  "flex w-full px-3 py-2 text-left text-sm " +
                  (i === highlight
                    ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                    : "text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700")
                }
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
